<script setup lang="ts">
import BaseInput from "@/components/base/BaseInput.vue";
import BaseButton from "@/components/base/BaseButton.vue";
import BaseSelect from "@/components/base/BaseSelect.vue";
import BaseDrop from "@/components/base/BaseDrop.vue";
import BaseCheckbox from "@/components/base/BaseCheckbox.vue";
import BaseModal from "@/components/base/BaseModal.vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useFeedback } from "@/composables/feedback/useFeedback";
import BaseFigureForIcon from "@/components/base/BaseFigureForIcon.vue";

const router = useRouter();
const { t } = useI18n();
const {
  files,
  form,
  isAccepted,
  isEmailError,
  isEmptyPhoneEmailWithErrors,
  isErrorModal,
  isSuccessModal,
  isValidPhone,
  isValidType,
  selectArr,
  sendForm,
  toggleModal,
  validateEmail,
} = useFeedback();

const goBack = () => {
  router.push("/settings");
};
</script>

<template>
  <div class="feedback">
    <div class="feedback-wrapper">
      <div class="feedback-row">
        <div class="feedback-header">
          <div class="feedback-header--back" @click="goBack">
            <BaseFigureForIcon
              size="medium"
              color="white"
              icon="arrow"
              position="left"
              is-button
            />
          </div>
          <div class="feedback-header--title">
            <p class="h4 bold">{{ t("feedback.title") }}</p>
          </div>
        </div>
        <div class="feedback-content">
          <div class="form-screen__body">
            <div class="feedback-content--form">
              <BaseSelect
                v-model="form.theme"
                :placeholder="t('feedback.themePlaceholder')"
                :items="selectArr"
              />
              <BaseInput
                v-model="form.type"
                @focus="isValidType = false"
                :has-error="isValidType"
                :max-length="20"
                type="text"
                :placeholder="t('feedback.deviceModelPlaceholder')"
              />
              <BaseInput
                v-model="form.phone"
                @focus="isValidPhone = false"
                :has-error="isValidPhone"
                :max-length="12"
                type="phone"
                :placeholder="t('feedback.phonePlaceholder')"
              />
              <BaseInput
                v-model="form.email"
                @focus="isValidType = false"
                @validation="validateEmail"
                :has-error="isEmailError"
                type="email"
                placeholder="Email"
              />
              <BaseInput
                v-model="form.comment"
                type="area"
                :placeholder="t('feedback.commentPlaceholder')"
              />
              <BaseDrop @update:files="files = $event" />
            </div>
            <div class="feedback-content--checkbox">
              <BaseCheckbox v-model="isAccepted" />
            </div>
          </div>
          <div class="feedback-content--btn form-screen__footer">
            <BaseButton
              :title="t('feedback.submit')"
              :disabled="isEmptyPhoneEmailWithErrors"
              @click="sendForm"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
  <Teleport to=".root">
    <BaseModal
      v-if="isErrorModal"
      :title="t('feedback.errorTitle')"
      :text="t('feedback.errorText')"
      :main-btn-title="t('common.actions.ok')"
      @close="toggleModal(false, 'error')"
      @action="toggleModal(false, 'error')"
    />
    <BaseModal
      v-if="isSuccessModal"
      :title="t('feedback.successTitle')"
      :text="t('feedback.successText')"
      :main-btn-title="t('feedback.successAction')"
      @close="toggleModal(false, 'success')"
      @action="toggleModal(false, 'success')"
    />
  </Teleport>
</template>
<style lang="scss" scoped>
@import "@/assets/style/colors";

.feedback {
  width: 100%;

  // Страница лежит под шапкой, поэтому фиксированную высоту от вьюпорта здесь
  // задавать нельзя. Скроллится документ, а кнопка отправки держится внизу
  // экрана через position: sticky (.form-screen__footer). Важно: ни у одного
  // предка футера не должно быть overflow: hidden — иначе sticky отключится.

  &-wrapper {
    display: flex;
    padding: 44px 0;

    @media (max-width: 768px) {
      padding: 17px 0 0;
    }
  }

  &-row {
    display: flex;
    flex: 1;
    flex-flow: row wrap;
    max-width: 720px;

    @media (max-width: 768px) {
      flex-flow: column;
    }
  }

  &-header {
    width: 100%;
    display: grid;
    grid-template-rows: 37px;
    grid-template-columns: auto 1fr auto;

    &--back {
      cursor: pointer;
      width: fit-content;
    }

    &--title {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  &-content {
    margin-top: 32px;
    padding: 24px 20px;
    border-radius: 16px;
    background-color: var(--color-white);
    display: flex;
    flex-flow: column;
    flex: 1;

    @media (max-width: 768px) {
      margin-top: 16px;
      padding-bottom: 0;
      // Запас снизу, чтобы последний блок формы не прятался под липкой кнопкой.
      margin-bottom: var(--bottom-nav-height);
    }

    &--form {
      display: flex;
      flex-flow: column;
      gap: 12px;
    }

    &--btn {
      margin-top: 16px;

      @media (max-width: 768px) {
        background: var(--color-white);
      }
    }

    &--checkbox {
      margin-top: 20px;
    }
  }
}
</style>
